import { useState } from 'react'
import { useParams } from 'react-router-dom'

import { Box, Divider, Text } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { OneModal, Textarea } from '@factorialco/f0-react/dist/experimental'
import {
  IntegrationsJoinCreateOrderDocument,
  IntegrationsJoinPremiumOfferTypesEnum,
  JoinPremiumBundleFragment,
  JoinPremiumOfferPricingFragment,
  JoinPremiumProductFragment,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { usePricingUtils } from 'modules/ats/pages/JobPostingMarketplace/usePricingUtils'

interface Props {
  premiumProduct?: JoinPremiumProductFragment
  premiumBundle?: JoinPremiumBundleFragment
  offerId: string
  offerName: string
  offerType: IntegrationsJoinPremiumOfferTypesEnum
  offerProductName: string
  pricing: JoinPremiumOfferPricingFragment
  onClose: () => void
  isOpen: boolean
}

export const PremiumOfferPurchaseModal = ({
  premiumProduct,
  premiumBundle,
  offerId,
  offerName,
  offerProductName,
  offerType,
  pricing,
  onClose,
  isOpen,
}: Props) => {
  const { jobId } = useParams()
  const { company, access } = useCurrentSession()
  const { formatPrice } = usePricingUtils()
  const [requesterNote, setRequesterNote] = useState('')
  const [createOrder, { loading: creatingOrder }] = useMutation(
    IntegrationsJoinCreateOrderDocument,
    {
      extractorFn: (response) => response.integrationsJoinMutations.createIntegrationsjoinOrder,
    }
  )
  const isBundle = offerType === 'bundle'
  const hasDiscount = !!pricing.reducedPercentage
  const price = hasDiscount ? pricing.reducedAmount : pricing.originalAmount
  const discountAmount = pricing.originalAmount - pricing.reducedAmount
  const formattedPrice = formatPrice(price, pricing.currencyCode)

  const buildBundleSnapshotProducts = () => {
    if (!premiumBundle) throw new Error('Bundle not passed as prop')

    return premiumBundle.products.map((product) => ({
      description: product.description,
      durationDays: product.durationDays,
      jobBoard: {
        slug: product.jobBoard.slug,
        name: product.jobBoard.name,
        logoUrl: product.jobBoard.logoUrl,
      },
    }))
  }

  const buildProductSnapshotProducts = () => {
    if (!premiumProduct) throw new Error('Product not passed as prop')

    const product = premiumProduct.products.find((p) => p.id === offerId)
    if (!product) throw new Error(`Could not find product with id ${offerId}`)

    return [
      {
        description: premiumProduct.description,
        durationDays: product.durationDays,
        jobBoard: {
          slug: premiumProduct.slug,
          name: premiumProduct.name,
          logoUrl: premiumProduct.logoUrl,
        },
      },
    ]
  }

  const handleCreateOrder = async () => {
    const response = await createOrder({
      variables: {
        accessId: access.id,
        atsJobPostingId: Number(jobId),
        premiumOfferId: offerId,
        premiumOfferName: offerName,
        premiumOfferPrice: price,
        premiumOfferType: offerType,
        note: requesterNote,
        snapshot: {
          price,
          currency: pricing.currencyCode,
          products: isBundle ? buildBundleSnapshotProducts() : buildProductSnapshotProducts(),
        },
      },
    })

    const handled = await handleResult(response, (error) => {
      app.setFlash({
        type: 'wrong',
        message: error.messages || i18n.t('common.save_error'),
      })
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })

      track(trackingEvents.ats.premiumOfferPurchased, {
        company_id: company.id,
        offer_id: offerId,
        offer_name: offerName,
        offer_type: offerType,
        offer_product_name: offerProductName,
        price,
      })
      onClose()
    }

    return handled
  }

  return (
    <OneModal onClose={onClose} isOpen={isOpen}>
      <OneModal.Header title={i18n.t('ats.job_details.marketplace.purchase_modal.title')} />
      <OneModal.Content withPadding>
        <Box gap="s16">
          {/* info section */}
          <Box flexDirection="row" justifyContent="spaceBetween" alignItems="center">
            <Box flexDirection="column" gap="s4" maxWidth="s400">
              <Text.Normal weight="semibold" color="grey1000">
                {offerName}
              </Text.Normal>
              <Text.Normal size="200" color="grey700">
                {offerProductName}
              </Text.Normal>
            </Box>
            <Text.Normal size="300" weight="semibold">
              {formattedPrice}
            </Text.Normal>
          </Box>
          {/* price discount section */}
          {hasDiscount && (
            <>
              <Divider backgroundColor="grey200" />
              <Box gap="s8">
                <Box flexDirection="row" justifyContent="spaceBetween" alignItems="center">
                  <Text.Normal size="200" color="grey700">
                    {i18n.t('ats.job_details.marketplace.purchase_modal.original_price')}
                  </Text.Normal>
                  <Text.Normal size="300" weight="semibold">
                    {formatPrice(pricing.originalAmount, pricing.currencyCode)}
                  </Text.Normal>
                </Box>
                <Box flexDirection="row" justifyContent="spaceBetween" alignItems="center">
                  <Text.Normal size="200" color="grey700">
                    {i18n.t('ats.job_details.marketplace.purchase_modal.discount')}
                  </Text.Normal>
                  <Text.Normal size="300" weight="semibold" color="olive1000">
                    -{formatPrice(discountAmount, pricing.currencyCode)}
                  </Text.Normal>
                </Box>
              </Box>
            </>
          )}
          <Divider backgroundColor="grey200" />
          {/* total purchase section */}
          <Box flexDirection="row" justifyContent="spaceBetween">
            <Box flexDirection="column" gap="s4">
              <Text.Normal weight="semibold" color="grey1000">
                {i18n.t('ats.job_details.marketplace.purchase_modal.total_purchase_title')}
              </Text.Normal>
              <Text.Normal size="200" color="grey700">
                {i18n.t('ats.job_details.marketplace.purchase_modal.taxes_copy')}
              </Text.Normal>
            </Box>
            <Text.Normal size="400" weight="semibold" color="grey1000">
              {formattedPrice}
            </Text.Normal>
          </Box>

          <Textarea
            label={i18n.t('ats.job_details.marketplace.purchase_modal.comments_title')}
            placeholder={i18n.t('ats.job_details.marketplace.purchase_modal.comments_placeholder')}
            onChange={setRequesterNote}
            value={requesterNote}
            rows={4}
          />
        </Box>
      </OneModal.Content>
      <OneModal.Footer>
        <Box flexDirection="row" gap="s12" justifyContent="flexEnd" width="full">
          <F0Button onClick={onClose} label={i18n.t('common.cancel')} variant="outline" />
          {/* TODO: add request creation 👇🏼 */}
          <F0Button
            type="submit"
            label={i18n.t('ats.job_details.marketplace.purchase_modal.request_payment_button')}
            loading={creatingOrder}
            onClick={handleCreateOrder}
          />
        </Box>
      </OneModal.Footer>
    </OneModal>
  )
}
